"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

const COOKIE_CONSENT_KEY = "3940:cookie_consent";

export interface CookieConsent {
  necessary: true;
  analytics: boolean;
  timestamp: string;
  version: string;
}

const CURRENT_VERSION = "v1.1";

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as CookieConsent;
  } catch {
    return null;
  }
}

export function saveCookieConsent(analytics: boolean): CookieConsent {
  const full: CookieConsent = {
    necessary: true,
    analytics,
    timestamp: new Date().toISOString(),
    version: CURRENT_VERSION,
  };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(full));
  return full;
}

export function hasAnalyticsConsent(): boolean {
  const consent = getCookieConsent();
  // Default true — analytics attivi salvo opt-out esplicito
  if (!consent) return true;
  return consent.analytics;
}

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    saveCookieConsent(true);
    setShowBanner(false);
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: { analytics: true } }));
  };

  const handleOptOut = () => {
    saveCookieConsent(false);
    setShowBanner(false);
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: { analytics: false } }));
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg"
      role="dialog"
      aria-label="Banner consenso cookie"
    >
      <div className="max-w-3xl mx-auto">
        {!showDetails ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <p className="flex-1 text-sm text-slate-700">
              🍪 Usiamo cookie tecnici e analytics anonimi per migliorare il servizio.{" "}
              <Link href="/legal/cookies" className="text-blue-600 hover:underline">
                Cookie Policy
              </Link>
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 transition"
              >
                Gestisci
              </button>
              <button
                onClick={handleAccept}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Preferenze Cookie</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Chiudi"
              >✕</button>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input type="checkbox" checked disabled className="mt-1 h-4 w-4 rounded border-slate-300" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 text-sm">Cookie Tecnici</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Obbligatori</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">Autenticazione e preferenze. Indispensabili al funzionamento.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input type="checkbox" checked disabled className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 text-sm">Cookie Analytics</span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Attivi per default</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Statistiche anonime e aggregate (Vercel Analytics). Non identificano l'utente.
                    Puoi disattivarli.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-slate-200">
              <Link href="/legal/cookies" className="text-xs text-blue-600 hover:underline">
                Maggiori informazioni
              </Link>
              <div className="flex gap-2">
                <button
                  onClick={handleOptOut}
                  className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 transition"
                >
                  Solo tecnici
                </button>
                <button
                  onClick={handleAccept}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function CookieSettingsLink({ className = "" }: { className?: string }) {
  const handleClick = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.location.reload();
  };
  return (
    <button onClick={handleClick} className={className}>
      Gestisci Cookie
    </button>
  );
}
